/**
 * Recovery from stale dynamic-import ("chunk") failures.
 *
 * Vite hashes chunk filenames, so a deployment invalidates every filename the
 * currently open tab knows about. When that tab lazy-loads a route or an
 * async component afterwards it requests a file that no longer exists, and
 * each browser words the failure differently:
 *
 *   Chrome/Firefox  "Failed to fetch dynamically imported module: <url>"
 *   Firefox         "error loading dynamically imported module"
 *   Safari          "'text/html' is not a valid JavaScript MIME type."
 *   older Safari    "Importing a module script failed."
 *
 * Safari's wording appears when the server answers a missing asset with an
 * HTML document instead of a 404, which is why the MIME patterns have to be
 * matched here as well as fixed server-side.
 *
 * Three call sites feed into this module, because no single one sees every
 * case: the window listeners in main.ts, router.onError for lazy route
 * components, and Vue's app.config.errorHandler for defineAsyncComponent
 * (Vue catches that rejection itself, so it never reaches the window).
 */
import { isClient, safeSessionStorage } from '@/composables/useSSR'

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'Loading chunk',
  'Loading CSS chunk',
  'ChunkLoadError',
  // Covers Safari's "'text/html' is not a valid JavaScript MIME type." and
  // Chrome's "Refused to execute script ... disallowed MIME type".
  'MIME type'
]

/**
 * Marks the last recovery reload so a permanently broken asset can't loop.
 * The same key and cooldown are reimplemented inline in index.html and
 * index.ssr.html, which have to guard before this module can be loaded at all.
 */
const RELOAD_MARKER_KEY = 'itsh_chunk_reload_at'
const RELOAD_COOLDOWN_MS = 30_000

/**
 * Fallback for when sessionStorage is unavailable (blocked by privacy
 * settings, storage partitioning, quota). Without it the cooldown would be a
 * no-op and a broken asset could reload without bound; this still bounds it to
 * one reload per page life, which is all a module-scoped value can promise.
 */
let lastReloadInMemory = 0

/**
 * Flattens an unknown thrown value into a string we can pattern-match.
 * Includes the error name so `ChunkLoadError` matches without a separate check.
 */
function toMessage(error: unknown): string {
  if (typeof error === 'string') return error

  if (error && typeof error === 'object') {
    const { name, message } = error as { name?: unknown; message?: unknown }
    const parts = [name, message].filter((part): part is string => typeof part === 'string')
    if (parts.length > 0) return parts.join(': ')
  }

  return String(error)
}

/**
 * True when the error is a failed dynamic import rather than an app bug.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (error === null || error === undefined) return false

  const message = toMessage(error)
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

/**
 * Reloads the page to pick up the current build when `error` is a stale-chunk
 * failure. Pass `target` to land on a different URL than the current one (the
 * router knows where the user was navigating to when the import failed).
 *
 * Returns true when a reload was triggered, so callers can suppress the error.
 * Returns false if the error is unrelated, or if we already reloaded moments
 * ago: a chunk that keeps failing after a fresh load is not stale but broken,
 * and looping on it would hide that. In that case the error is left to
 * propagate so it reaches error tracking.
 */
export function recoverFromChunkError(error: unknown, target?: string): boolean {
  if (!isClient || !isChunkLoadError(error)) return false

  const storage = safeSessionStorage()
  const stored = Number(storage?.getItem(RELOAD_MARKER_KEY) ?? '')
  // Storage is authoritative when available: unlike the in-memory value it
  // survives the reload it is meant to guard against.
  const lastReload = storage ? (Number.isFinite(stored) ? stored : 0) : lastReloadInMemory
  const now = Date.now()
  const sinceLastReload = now - lastReload

  // A negative age means the clock moved backwards (NTP correction, VM resume)
  // rather than that we are inside the cooldown, and must not wedge recovery.
  if (lastReload > 0 && sinceLastReload >= 0 && sinceLastReload < RELOAD_COOLDOWN_MS) {
    // Either a reload already happened and did not help, or the inline guard in
    // the HTML shell just claimed this same event: it is registered first, so
    // on a window-level chunk error it writes the marker and reloads before
    // this runs. Both cases mean not reloading again.
    console.error('[ChunkError] Reload already attempted, not retrying:', error)
    return false
  }

  storage?.setItem(RELOAD_MARKER_KEY, String(now))
  lastReloadInMemory = now
  console.warn('[ChunkError] Stale assets detected, reloading...')

  if (target) {
    window.location.href = target
  } else {
    window.location.reload()
  }
  return true
}
